/**
 * Gertsak's mark.
 *
 * The logo is a wordmark — `Gertsak` with a blue blade in place of the upper
 * arm of the «k» — so the product has two marks: the wordmark itself, for
 * every place with room for it, and a square mark cut from its «k», for
 * places without (a favicon, a collapsed rail, a shop's own name beside it).
 * No screen draws either; they all come from here.
 *
 * The wordmark files are raster, cut from the supplied artwork. When a vector
 * master arrives, replace the two files in `../assets` and nothing else.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../cn';
import wordmarkDark from '../assets/gertsak-wordmark-dark.png';
import wordmarkLight from '../assets/gertsak-wordmark-light.png';

export const BRAND_NAME = 'Gertsak';

/** The square mark: the logo's «k», white stem and leg, blue blade, on night. */
function DefaultMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <rect width="32" height="32" rx="8" fill="#0a0d14" />
      <rect x="9" y="6.5" width="4.6" height="19" rx="1.4" fill="#fff" />
      <path d="M13.6 17.2 20.3 25.5h5.4L17.9 15.8Z" fill="#fff" />
      <path d="M14.6 16.4 21.6 8.2h5.6L20.4 16.4Z" fill="#0e74ff" />
    </svg>
  );
}

const LogoContext = createContext<((props: { className?: string }) => ReactNode) | null>(null);

/**
 * Supplies the real logo to every `Brand` below it. Wrap the app root once;
 * without it the placeholder glyph is used.
 */
export function BrandLogoProvider({
  logo,
  children,
}: {
  logo: (props: { className?: string }) => ReactNode;
  children: ReactNode;
}) {
  return <LogoContext.Provider value={logo}>{children}</LogoContext.Provider>;
}

export interface BrandProps {
  /**
   * What to show instead of the product name — a shop's own name on the
   * register, for instance. The mark stays; only the words change.
   */
  name?: string;
  subtitle?: string;
  /** `sm` for a top bar, `lg` for sign-in and the customer display. */
  size?: 'sm' | 'md' | 'lg';
  /** Mark only, for tight places. The name still reaches screen readers. */
  markOnly?: boolean;
  className?: string;
}

const markSizes = { sm: 'size-7', md: 'size-8', lg: 'size-12' };
const nameSizes = { sm: 'text-base leading-5', md: 'text-base leading-5', lg: 'text-xl leading-7' };

/**
 * The product mark. Used by every shell; never drawn inline by a screen.
 *
 * Without a `name` it is the wordmark — the name *is* the logo, so a square
 * mark beside the word "Gertsak" would say it twice. With a shop's name, the
 * square mark stands for the product and the words belong to the shop.
 */
export function Brand({ name, subtitle, size = 'md', markOnly, className }: BrandProps) {
  const Logo = useContext(LogoContext) ?? DefaultMark;
  const label = name ?? BRAND_NAME;
  if (!name && !markOnly) {
    return (
      <div className={cn('min-w-0 leading-tight', className)}>
        <Wordmark size={size === 'lg' ? 'lg' : 'md'} />
        {subtitle && <p className="mt-1 truncate text-xs text-ink-3">{subtitle}</p>}
      </div>
    );
  }
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <span className={cn('shrink-0', markSizes[size])}>
        <Logo className="size-full" />
      </span>
      {markOnly ? (
        <span className="sr-only">{label}</span>
      ) : (
        <div className="min-w-0 leading-tight">
          <p className={cn('line-clamp-2 font-semibold tracking-tight text-ink', nameSizes[size])}>
            {label}
          </p>
          {subtitle && <p className="truncate text-xs text-ink-3">{subtitle}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * The wordmark: the logo itself. `tone` picks the file — dark letters for
 * light ground (the default), white letters for dark ground. The blue blade is
 * the same in both.
 */
export function Wordmark({
  size = 'md',
  tone = 'dark',
  className,
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  tone?: 'dark' | 'light';
  className?: string;
}) {
  const heights = { sm: 'h-4', md: 'h-5', lg: 'h-7', xl: 'h-10' };
  return (
    <img
      src={tone === 'light' ? wordmarkLight : wordmarkDark}
      alt={BRAND_NAME}
      draggable={false}
      className={cn('block w-auto select-none', heights[size], className)}
    />
  );
}
